import React, { useState } from 'react';

export default function Step3DrivingLicence({ formData, files, errors, updateField, updateFile, clearError, setError }) {
  const h = (e) => { updateField(e.target.name, e.target.value); clearError(e.target.name); };
  const hf = (e) => { updateFile(e.target.name, e.target.files[0]); clearError(e.target.name); };
  const handleLicenceNumber = (e) => {
    const val = e.target.value.replace(/\s/g, '').toUpperCase().slice(0, 16);
    updateField('licence_number', val);
    clearError('licence_number');
  };

  // Licence issue date — DD/MM/YYYY text inputs (cross-browser mobile safe)
  const initLid = () => {
    const v = formData.licence_issue_date || '';
    const [y, m, d] = v.split('-');
    return { day: d || '', month: m || '', year: y || '' };
  };
  const [lidParts, setLidParts] = useState(initLid);

  const handleLid = (part, rawVal) => {
    const maxLen = part === 'year' ? 4 : 2;
    const val = rawVal.replace(/\D/g, '').slice(0, maxLen);
    const next = { ...lidParts, [part]: val };
    setLidParts(next);
    if (next.day && next.month && next.year.length === 4) {
      const iso = `${next.year}-${next.month.padStart(2, '0')}-${next.day.padStart(2, '0')}`;
      updateField('licence_issue_date', iso);
      // Inline blur validation
      const issued = new Date(iso);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      if (issued > today)
        setError('licence_issue_date', 'Licence issue date cannot be in the future');
      else if (issued < new Date('1967-01-01'))
        setError('licence_issue_date', 'Enter a valid issue date — UK driving licences were introduced in 1967');
      else
        clearError('licence_issue_date');
    } else {
      updateField('licence_issue_date', '');
      clearError('licence_issue_date');
    }
  };

  const handleDvlaCheckCodeBlur = () => {
    const val = (formData.dvla_check_code || '').trim();
    if (!val) return;
    const code = val.replace(/\s/g, '');
    if (code.length !== 8)
      setError('dvla_check_code', 'DVLA check code must be exactly 8 characters (e.g. 44gxXYrz)');
    else if (!/^[A-Z0-9]{8}$/i.test(code))
      setError('dvla_check_code', 'DVLA check code can only contain letters and numbers');
    else
      clearError('dvla_check_code');
  };

  return (
    <div className="adi-step-content">
      <div className="adi-step-header">
        <span className="adi-step-num-badge">3</span>
        <div>
          <h2 className="adi-step-title">Driving Licence Verification</h2>
          <p className="adi-step-desc">Your UK driving licence details and document uploads</p>
        </div>
      </div>

      <div className="adi-field-grid">

        <div className="adi-field adi-field--full">
          <label>UK Driving Licence Number *</label>
          <input name="licence_number" value={formData.licence_number || ''} onChange={handleLicenceNumber}
            placeholder="e.g. SMITH731021AB9XY"
            style={{ textTransform: 'uppercase', letterSpacing: '2px', fontFamily: 'monospace' }}
            maxLength={16}
            className={errors.licence_number ? 'err' : ''} />
          {errors.licence_number
            ? <span className="adi-err">{errors.licence_number}</span>
            : <p className="adi-field-note">
                Exactly 16 characters — e.g. <strong>SMITH731021AB9XY</strong><br />
                5 surname chars · 2-digit year · 2-digit month (female +50) · 2-digit day · 2 initials · 2 DVLA chars · 1 check letter
              </p>
          }
        </div>

        <div className="adi-field adi-field--full">
          <label>DVLA Check Code *</label>
          <input
            name="dvla_check_code"
            value={formData.dvla_check_code || ''}
            onChange={h}
            onBlur={handleDvlaCheckCodeBlur}
            placeholder="e.g. 44gxXYrz"
            style={{ letterSpacing: '2px', fontFamily: 'monospace' }}
            className={errors.dvla_check_code ? 'err' : ''}
          />
          {errors.dvla_check_code
            ? <span className="adi-err">{errors.dvla_check_code}</span>
            : <p className="adi-field-note">
                This code expires in 21 days. Generate your check code from{' '}
                <a href="https://www.gov.uk/view-driving-licence" target="_blank" rel="noopener noreferrer">
                  this link
                </a>
                {' '}on the DVLA website. Code is 8 characters (letters &amp; digits).
              </p>
          }
        </div>

        <div className="adi-field">
          <label>Licence Issue Date <em style={{ fontWeight: 400, fontSize: '0.8rem' }}>(DD / MM / YYYY)</em></label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.8fr', gap: '0.5rem' }}>
            <input
              type="text"
              inputMode="numeric"
              placeholder="DD"
              value={lidParts.day}
              onChange={e => handleLid('day', e.target.value)}
              className={errors.licence_issue_date ? 'err' : ''}
              maxLength={2}
            />
            <input
              type="text"
              inputMode="numeric"
              placeholder="MM"
              value={lidParts.month}
              onChange={e => handleLid('month', e.target.value)}
              className={errors.licence_issue_date ? 'err' : ''}
              maxLength={2}
            />
            <input
              type="text"
              inputMode="numeric"
              placeholder="YYYY"
              value={lidParts.year}
              onChange={e => handleLid('year', e.target.value)}
              className={errors.licence_issue_date ? 'err' : ''}
              maxLength={4}
            />
          </div>
          <p className="adi-field-note">Optional — date shown on your photocard licence · e.g. 12 06 2015</p>
          {errors.licence_issue_date && <span className="adi-err">{errors.licence_issue_date}</span>}
        </div>

        <div className="adi-field adi-upload-pair-label adi-field--full">
          <p className="adi-upload-section-title">Upload Driving Licence (both sides required) *</p>
          <p className="adi-upload-hint">Please photograph your photocard licence clearly on a plain background. Both front and back are required.</p>
        </div>

        <div className="adi-field adi-field--upload">
          <label>Driving Licence – Front *</label>
          <div className={`adi-upload-box${errors.driving_licence_front ? ' adi-upload-box--err' : ''}`}>
            <input type="file" name="driving_licence_front" onChange={hf} accept="image/*,.pdf" />
            <div className="adi-upload-label">
              {files.driving_licence_front ? (
                <span className="adi-upload-selected">✓ {files.driving_licence_front.name}</span>
              ) : (
                <><span className="adi-upload-icon">📷</span> Front of licence</>
              )}
            </div>
          </div>
          {errors.driving_licence_front && <span className="adi-err">{errors.driving_licence_front}</span>}
        </div>

        <div className="adi-field adi-field--upload">
          <label>Driving Licence – Back *</label>
          <div className={`adi-upload-box${errors.driving_licence_back ? ' adi-upload-box--err' : ''}`}>
            <input type="file" name="driving_licence_back" onChange={hf} accept="image/*,.pdf" />
            <div className="adi-upload-label">
              {files.driving_licence_back ? (
                <span className="adi-upload-selected">✓ {files.driving_licence_back.name}</span>
              ) : (
                <><span className="adi-upload-icon">📷</span> Back of licence</>
              )}
            </div>
          </div>
          {errors.driving_licence_back && <span className="adi-err">{errors.driving_licence_back}</span>}
        </div>

      </div>
    </div>
  );
}
